import * as React from 'react';
import { AppStoreType, IntegrationType, IssueBuild, IssueSource, IssueSourceType, IssueState } from '@/types';
import { useSelector } from 'react-redux';
import { getIntegrationTypeForAppStoreType } from '@/helpers';
import { BuildSummary, getBuildsForVersion } from '@/api';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials } from '@/selectors/selectors';
import CIAppStoreBuildComponent from '@/components/CIAppStoreBuildComponent';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';

export const CLOSED_ISSUE_STATES = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

export const iconForIssueSource = (
    source: IssueSource | undefined,
    appStoreType: AppStoreType | undefined,
    size = 16,
): React.ReactNode => {
    if (source?.type === IssueSourceType.Runway) {
        return <img src={runwayLogo} alt="Runway" width={size} height={size} />;
    }
    const derivedIntegrationType =
        source?.type === IssueSourceType.UserReviews && appStoreType ? getIntegrationTypeForAppStoreType(appStoreType)
        : source?.type === IssueSourceType.BetaFeedback ? IntegrationType.TestFlight
        : null;
    return <IntegrationIcon integrationId={derivedIntegrationType ?? source?.integrationType} size={size} />;
};

export const useBuildsForVersion = (orgId: string, appId: string, version?: string) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [builds, setBuilds] = React.useState<BuildSummary[]>([]);
    const [buildsLoading, setBuildsLoading] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (version) {
            setBuilds([]);
            setBuildsLoading(true);
            getBuildsForVersion(orgId, appId, version, auth)
                .then((res) => !cancelled && setBuilds(res.builds ?? []))
                .catch(() => !cancelled && setBuilds([]))
                .finally(() => !cancelled && setBuildsLoading(false));
        } else {
            setBuilds([]);
        }
        return () => {
            cancelled = true;
        };
    }, [version, orgId, appId, auth]);

    return { builds, buildsLoading };
};

export const buildSelectOptions = (builds: IssueBuild[]) =>
    builds.map((b) => ({
        value: b.identifier,
        searchLabel: [b.identifier, b.appStoreBuild].filter(Boolean).join(' '),
        label: (
            <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <CIAppStoreBuildComponent
                    ciBuildIdentifier={b.identifier}
                    ciBuildIntegrationId={b.integrationId as IntegrationType}
                    appStoreBuildIdentifier={b.appStoreBuild}
                    appStoreBuildIntegrationId={b.appStoreIntegrationId as IntegrationType}
                    isAppStoreBuildProcessed={b.appStoreBuildIsProcessed ?? true}
                    strong={false}
                />
            </span>
        ),
    }));
